import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { LocateFixed, Loader2, MapPin } from 'lucide-react';

const pinIcon = L.icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

// Default view: Metro Manila, Philippines
const DEFAULT_CENTER = { lat: 14.5995, lng: 120.9842 };

const LocationPicker = ({ value, onChange, height = 'h-64', focus = null }) => {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const marker = useRef(null);
  const [locating, setLocating] = useState(false);
  const [err, setErr] = useState('');

  const place = (lat, lng, recenter = false) => {
    if (!mapObj.current) return;
    if (!marker.current) {
      marker.current = L.marker([lat, lng], { icon: pinIcon, draggable: true }).addTo(mapObj.current);
      marker.current.on('dragend', () => {
        const p = marker.current.getLatLng();
        onChange({ lat: +p.lat.toFixed(6), lng: +p.lng.toFixed(6) });
      });
    } else {
      marker.current.setLatLng([lat, lng]);
    }
    if (recenter) mapObj.current.setView([lat, lng], Math.max(mapObj.current.getZoom(), 15));
  };

  useEffect(() => {
    if (!mapRef.current || mapObj.current) return;
    const start = value && value.lat != null ? value : DEFAULT_CENTER;
    mapObj.current = L.map(mapRef.current).setView([start.lat, start.lng], value && value.lat != null ? 15 : 11);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap' }).addTo(mapObj.current);
    mapObj.current.on('click', (e) => {
      place(e.latlng.lat, e.latlng.lng);
      onChange({ lat: +e.latlng.lat.toFixed(6), lng: +e.latlng.lng.toFixed(6) });
    });
    if (value && value.lat != null) place(value.lat, value.lng);
    setTimeout(() => mapObj.current && mapObj.current.invalidateSize(), 200);
    return () => { if (mapObj.current) { mapObj.current.remove(); mapObj.current = null; marker.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reflect external value changes (e.g. "use my location")
  useEffect(() => {
    if (value && value.lat != null && mapObj.current) place(value.lat, value.lng, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value && value.lat, value && value.lng]);

  // Zoom/recenter to an area (e.g. barangay of a saved location) WITHOUT placing a pin.
  useEffect(() => {
    if (focus && focus.lat != null && mapObj.current) {
      mapObj.current.setView([focus.lat, focus.lng], focus.zoom || 15);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus && focus.lat, focus && focus.lng, focus && focus.zoom]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { setErr('Geolocation not supported on this device.'); return; }
    setLocating(true); setErr('');
    const onOk = (pos) => {
      setLocating(false);
      const lat = +pos.coords.latitude.toFixed(6), lng = +pos.coords.longitude.toFixed(6);
      place(lat, lng, true);
      onChange({ lat, lng });
    };
    const describe = (e) => {
      if (e && e.code === 1) return 'Location permission denied. Please allow location access in your browser/app settings, then try again — or drop a pin on the map.';
      if (e && e.code === 2) return 'Your location is unavailable right now. Drop a pin on the map instead.';
      return 'Getting your location timed out. Drop a pin on the map, or try again.';
    };
    // First attempt: high accuracy. If it fails/times out, retry with low accuracy
    // (works far more reliably on many Android WebViews / laptops).
    navigator.geolocation.getCurrentPosition(
      onOk,
      () => {
        navigator.geolocation.getCurrentPosition(
          onOk,
          (e2) => { setLocating(false); setErr(describe(e2)); },
          { enableHighAccuracy: false, timeout: 20000, maximumAge: 60000 }
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  return (
    <div data-testid="location-picker">
      <div className="flex items-center justify-between mb-2 gap-2">
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5" /> Tap the map or drag the pin to set the exact service location.
        </p>
        <button type="button" onClick={useMyLocation} disabled={locating}
          className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-50 shrink-0"
          data-testid="use-my-location-btn">
          {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />} Use my location
        </button>
      </div>
      <div ref={mapRef} className={`w-full ${height} rounded-lg overflow-hidden border border-gray-300 z-0`} data-testid="location-picker-map" />
      {err && <p className="mt-1 text-xs text-red-600" data-testid="location-picker-error">{err}</p>}
      {value && value.lat != null ? (
        <p className="mt-1 text-xs text-green-700" data-testid="location-picker-coords">
          📍 Pinned: {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
        </p>
      ) : (
        <p className="mt-1 text-xs text-gray-400" data-testid="location-picker-empty">No location set yet.</p>
      )}
    </div>
  );
};

export default LocationPicker;
